// ─────────────────────────────────────────────────────────────────────────────
// API client for the Service screen (Phase 7.3).
//
// Why a hand-rolled fetch instead of the shared @matix/api-client axios
// instance: the cashier client doesn't carry per-endpoint ETag state.
// Adding ETag support there would mean touching every other call site in
// the cashier; here we have a single screen with one polled endpoint, so
// keeping the ETag bookkeeping local is simpler and won't leak.
//
// Behaviors:
//   - Adds Authorization: Bearer <jwt> from authClient
//   - Tracks the last-seen ETag per (method+url+queryString) tuple
//   - Replays it as If-None-Match on the next call
//   - Returns NotModified marker when the server replies 304 — caller keeps
//     showing the previous data
// ─────────────────────────────────────────────────────────────────────────────

import { getAuthToken } from './authClient';

/** Sentinel returned from `getJSON` when the server replies 304. */
export const NOT_MODIFIED = Symbol('NotModified');
export type NotModified = typeof NOT_MODIFIED;

/**
 * Per-endpoint ETag cache.
 *
 * Key shape: `${method} ${url}` — query string included so /orders?branch=46
 * and /orders?branch=47 each get their own cache entry. Cleared on 401
 * (auth failure) since the new session may see different orders.
 *
 * Bounded growth: in this app we only call /service/orders and /service/filters
 * — at most a handful of distinct keys. No eviction needed.
 */
const etagCache = new Map<string, string>();

/** Reset all cached ETags. Called on auth failure. */
export function clearEtagCache(): void {
  etagCache.clear();
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class AuthRequiredError extends Error {
  constructor() {
    super('Authentication required — log into the cashier app first.');
    this.name = 'AuthRequiredError';
  }
}

/**
 * GET a JSON endpoint with automatic ETag handling.
 *
 * On a normal 200, returns the parsed body (and caches the response's ETag).
 * On a 304, returns the NOT_MODIFIED sentinel — caller keeps prior state.
 * On any error status, throws an ApiError (or AuthRequiredError on 401).
 *
 * `force` skips the conditional request entirely, and exists because 304 means
 * "what you already have is current" — which is a lie the moment the caller has
 * thrown its copy away. A caller that has just emptied its state MUST pass it,
 * or the server will cheerfully confirm a snapshot that no longer exists
 * anywhere. That is not hypothetical: switching KDS view cleared the board and
 * then took a 304, so the board stayed empty until the next real change or a
 * re-login (which clears this cache). See `selectStation` in kdsStore.
 */
export async function getJSON<T>(
  path: string,
  opts: { force?: boolean } = {},
): Promise<T | NotModified> {
  const token = getAuthToken();
  if (!token) throw new AuthRequiredError();

  const cacheKey = `GET ${path}`;
  const headers: Record<string, string> = {
    accept: 'application/json',
    authorization: `Bearer ${token}`,
  };
  // Dropped rather than merely not sent: a forced fetch is the caller saying
  // the cached entry is worthless, so leaving it behind would let the very next
  // poll take a 304 against state we no longer hold.
  if (opts.force) etagCache.delete(cacheKey);
  const prevEtag = opts.force ? undefined : etagCache.get(cacheKey);
  if (prevEtag) headers['if-none-match'] = prevEtag;

  const resp = await fetch(path, { method: 'GET', headers });

  if (resp.status === 304) {
    // Server confirmed the snapshot we already have. Stay on it.
    return NOT_MODIFIED;
  }

  if (resp.status === 401) {
    clearEtagCache();
    throw new AuthRequiredError();
  }

  if (!resp.ok) {
    let body: unknown = null;
    try {
      body = await resp.json();
    } catch {
      // body wasn't JSON — leave null
    }
    throw new ApiError(resp.status, body, `HTTP ${resp.status} on GET ${path}`);
  }

  // Capture the new ETag for the next call. Header lookup is
  // case-insensitive in fetch's Headers, so 'etag' works for 'ETag' too.
  const newEtag = resp.headers.get('etag');
  if (newEtag) {
    etagCache.set(cacheKey, newEtag);
  } else {
    // No ETag means we shouldn't replay anything next time; clear the slot
    // so we don't accidentally send a stale If-None-Match for unrelated content.
    etagCache.delete(cacheKey);
  }

  return (await resp.json()) as T;
}

/**
 * POST a JSON endpoint. No ETag handling on writes — these are mutations
 * and the server always returns the new state.
 */
export async function postJSON<T>(path: string, body?: unknown): Promise<T> {
  const token = getAuthToken();
  if (!token) throw new AuthRequiredError();

  const headers: Record<string, string> = {
    accept: 'application/json',
    authorization: `Bearer ${token}`,
  };
  if (body !== undefined) headers['content-type'] = 'application/json';

  const resp = await fetch(path, {
    method: 'POST',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (resp.status === 401) {
    clearEtagCache();
    throw new AuthRequiredError();
  }

  if (!resp.ok) {
    let errBody: unknown = null;
    try {
      errBody = await resp.json();
    } catch {
      // ignore
    }
    throw new ApiError(resp.status, errBody, `HTTP ${resp.status} on POST ${path}`);
  }

  // 204 No Content is plausible for some bumps; guard the JSON parse.
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}
